import React, { useMemo, useState, useEffect } from 'react';
import { Building2, MapPin, FileText, Map as MapIcon, ExternalLink, PersonStanding, Link, Users, Camera, X, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Client } from './types';
import api from '../../../../services/api';
import { useAlert } from '../../../../components/AlertProvider';

interface CompanyTabProps {
  client: Client;
  onUpdate?: (updatedClient: Client) => void;
  centralPurchasingName?: string | null;
  onReloadClientData?: () => void;
  centralPurchasingId?: number | null;
}

export const CompanyTab: React.FC<CompanyTabProps> = ({ client, onUpdate, centralPurchasingName, onReloadClientData, centralPurchasingId }) => {
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [showCentralModal, setShowCentralModal] = useState(false);
  const [centralBuyers, setCentralBuyers] = useState<Client[]>([]);
  const [loadingCentrals, setLoadingCentrals] = useState(false);
  const [attachedClients, setAttachedClients] = useState<Client[]>([]);
  const [loadingAttachedClients, setLoadingAttachedClients] = useState(false);
  const [clientImages, setClientImages] = useState<{ photo_1_url?: string; photo_2_url?: string; photo_3_url?: string }>({});
  const [uploadingPhoto, setUploadingPhoto] = useState<number | null>(null);
  const { showError, showSuccess } = useAlert();
  const navigate = useNavigate();

  // Load client images on mount
  useEffect(() => {
    loadClientImages();
  }, [client.id]);

  const loadClientImages = async () => {
    try {
      const response = await api.get(`/client-images/${client.id}`);
      setClientImages({
        photo_1_url: response.data.photo_1_url,
        photo_2_url: response.data.photo_2_url,
        photo_3_url: response.data.photo_3_url,
      });
    } catch (e) {
      // Silently fail - images are optional
      console.log('No images found for client');
    }
  };

  const handlePhotoUpload = async (photoNumber: number, file: File) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showError('Format non supporté. Utilisez JPG, PNG ou WEBP');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      showError('Fichier trop volumineux. Maximum 5MB');
      return;
    }

    setUploadingPhoto(photoNumber);
    try {
      const formData = new FormData();
      formData.append('photo_number', photoNumber.toString());
      formData.append('file', file);

      const response = await api.post(`/client-images/${client.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setClientImages({
        photo_1_url: response.data.photo_1_url,
        photo_2_url: response.data.photo_2_url,
        photo_3_url: response.data.photo_3_url,
      });
      showSuccess(`Photo ${photoNumber} uploadée avec succès`);
    } catch (e) {
      showError('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handlePhotoDelete = async (photoNumber: number) => {
    try {
      await api.delete(`/client-images/${client.id}/photo/${photoNumber}`);
      setClientImages(prev => ({
        ...prev,
        [`photo_${photoNumber}_url`]: undefined
      }));
      showSuccess(`Photo ${photoNumber} supprimée`);
    } catch (e) {
      showError('Erreur lors de la suppression');
    }
  };

  const handleRdvChange = async (rdv: 'R0' | 'R1' | 'R2' | 'R3' | '') => {
    try {
      const response = await api.patch(`/clients/${client.id}`, { rdv_status: rdv || null });
      onUpdate?.(response.data);
    } catch (e) {
      showError('Erreur lors de la mise à jour');
    }
  };

  const handleCoteChange = async (cote: 'Glacier' | 'Froid' | 'Tiède' | 'Chaud' | '') => {
    try {
      const response = await api.patch(`/clients/${client.id}`, { cote: cote || null });
      onUpdate?.(response.data);
    } catch (e) {
      showError('Erreur lors de la mise à jour');
    }
  };

  const handleGoldMedalChange = async (enabled: boolean) => {
    try {
      const response = await api.patch(`/clients/${client.id}`, { gold_medal: enabled });
      onUpdate?.(response.data);
    } catch (e) {
      showError('Erreur lors de la mise à jour');
    }
  };

  const loadCentralBuyers = async () => {
    try {
      setLoadingCentrals(true);
      console.log('Loading central buyers...');

      let centrals = [];
      let lastError = null;

      // Try first endpoint: /clients/centrales
      try {
        const res = await api.get('/clients/centrales');
        console.log('Response from /clients/centrales:', res);
        centrals = Array.isArray(res.data) ? res.data : res.data?.items || res.data?.clients || [];
      } catch (firstError: any) {
        lastError = firstError;
        console.log('First endpoint /clients/centrales failed:', firstError.response?.status);

        // Try second endpoint: /clients with filtering
        try {
          const res = await api.get('/clients');
          console.log('Response from /clients:', res);
          const allClients = Array.isArray(res.data) ? res.data : res.data?.items || res.data?.clients || [];
          centrals = allClients.filter((client: any) =>
            client.client_type === 'centrale' || client.client_type === 'central'
          );
        } catch (secondError: any) {
          lastError = secondError;
          console.log('Second endpoint /clients also failed:', secondError.response?.status);

          // Try third fallback: get all clients without filter (in case filtering is the issue)
          try {
            const res = await api.get('/clients', {
              params: { limit: 1000 } // Get more clients to find centrals
            });
            console.log('Response from /clients (no filter):', res);
            const allClients = Array.isArray(res.data) ? res.data : res.data?.items || res.data?.clients || [];
            centrals = allClients.filter((client: any) =>
              client.client_type === 'centrale' ||
              client.client_type === 'central' ||
              (client.type && (client.type === 'centrale' || client.type === 'central'))
            );
          } catch (thirdError: any) {
            lastError = thirdError;
            console.error('All endpoints failed. Last error:', thirdError);

            // As final fallback, provide empty array and user-friendly message
            centrals = [];
          }
        }
      }

      console.log('Final centrals list:', centrals);
      setCentralBuyers(centrals);

      if (centrals.length === 0) {
        if (lastError?.response?.status >= 500) {
          showError('Service temporairement indisponible. Veuillez réessayer plus tard.');
        } else {
          showError('Aucune centrale d\'achat disponible pour le moment.');
        }
      } else {
        console.log(`Found ${centrals.length} central(s)`);
      }
    } catch (e: any) {
      console.error('Unexpected error in loadCentralBuyers:', e);
      showError('Une erreur inattendue s\'est produite. Veuillez réessayer.');
      setCentralBuyers([]);
    } finally {
      setLoadingCentrals(false);
    }
  };

  const handleCentralAssignment = async (centralId: number | null) => {
    try {
      const response = await api.patch(`/clients/${client.id}`, {
        central_purchasing_id: centralId,
        use_central_purchasing_bpu: centralId ? true : false
      });
      onUpdate?.(response.data);
      setShowCentralModal(false);
      showSuccess(centralId ? 'Client rattaché à la centrale d\'achat' : 'Rattachement supprimé');
      onReloadClientData?.();
    } catch (e) {
      showError('Erreur lors de la mise à jour du rattachement');
    }
  };

  const loadAttachedClients = async () => {
    try {
      setLoadingAttachedClients(true);
      console.log('Loading attached clients for central ID:', client.id);

      const res = await api.get('/clients');
      console.log('All clients API response:', res.data);

      const allClients = Array.isArray(res.data) ? res.data : res.data?.items || res.data?.clients || [];
      console.log('All clients array:', allClients);

      // Filter clients that have this purchasing center's ID as their central_purchasing_id
      // Exclude other purchasing centers (centrales) from the list
      const filteredClients = allClients.filter((c: Client) => {
        console.log(`Client ${c.company_name}: central_purchasing_id=${c.central_purchasing_id}, client_type=${c.client_type}, looking for=${client.id}`);
        return c.central_purchasing_id === client.id &&
          c.client_type !== 'centrale' &&
          c.client_type !== 'central';
      });

      console.log('Final filtered attached clients:', filteredClients);
      setAttachedClients(filteredClients);
    } catch (e) {
      console.error('Load attached clients error:', e);
      showError('Erreur lors du chargement des clients rattachés');
      setAttachedClients([]);
    } finally {
      setLoadingAttachedClients(false);
    }
  };

  // Load attached clients when component mounts if this is a purchasing center
  useEffect(() => {
    if (client.client_type === 'centrale' || client.client_type === 'central') {
      loadAttachedClients();
    }
  }, [client.id, client.client_type]);

  // Also refresh attached clients periodically to catch updates from other pages
  useEffect(() => {
    if (client.client_type === 'centrale' || client.client_type === 'central') {
      const interval = setInterval(() => {
        loadAttachedClients();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [client.id, client.client_type]);

  const addressComplete = useMemo(() => {
    const parts = [client.address, client.postal_code, client.city, client.country]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean);
    return parts.join(' ');
  }, [client.address, client.city, client.country, client.postal_code]);

  const mapsLink = useMemo(() => {
    if (!addressComplete) return '';
    return `https://www.google.com/maps/search/${encodeURIComponent(addressComplete)}`;
  }, [addressComplete]);

  const mapsEmbedSrc = useMemo(() => {
    if (!addressComplete) return '';
    return `https://www.google.com/maps?q=${encodeURIComponent(addressComplete)}&output=embed`;
  }, [addressComplete]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
        <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border sm:border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 sm:mb-6 flex items-center gap-2">
            <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            Informations Générales
          </h2>
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Nom de l'entreprise</label>
              <p className="text-slate-900 dark:text-slate-100 font-medium">{client.company_name}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Type Client</label>
                <p className="text-slate-900 dark:text-slate-100 font-medium capitalize">{client.client_type}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Marque</label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">{client.marque || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">SIRET</label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">{client.siret || '—'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Site web</label>
                {client.website ? (
                  <a
                    href={client.website}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Voir
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">—</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Pappers.fr</label>
                {client.pappers_url ? (
                  <a
                    href={client.pappers_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Voir
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">—</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Infogreffe</label>
                {client.infogreffe_url ? (
                  <a
                    href={client.infogreffe_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Voir
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">—</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Pharmanity</label>
                {client.pharmanity_url ? (
                  <a
                    href={client.pharmanity_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    Voir
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <p className="text-slate-900 dark:text-slate-100 font-medium">—</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Date de création</label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">
                  {client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  }) : '—'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Dernière connexion</label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">
                  {client.last_login
                    ? new Date(client.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(client.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                    : <span className="italic text-slate-400 font-normal">Jamais connecté</span>
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right column: R1/R2/R3 + Cote box ABOVE Localisation */}
        <div className="flex flex-col gap-6">
          {/* Section Suivi Commercial - R1/R2/R3 et Cote */}
          <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border-l-4 border-l-yellow-400 border border-slate-200 dark:border-slate-700 p-2.5 sm:p-3">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Icon */}
              <div className="flex-shrink-0">
                <PersonStanding className="h-7 w-7 sm:h-10 sm:w-10 text-green-600" />
              </div>

              {/* Rendez-vous R0/R1/R2/R3 - Clickable */}
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => handleRdvChange(client.rdv_status === 'R0' ? '' : 'R0')}
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className={`w-3.5 h-3.5 rounded-full ${client.rdv_status === 'R0' ? 'bg-slate-800 dark:bg-slate-200' : 'border-2 border-slate-400'}`} />
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">R0</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleRdvChange(client.rdv_status === 'R1' ? '' : 'R1')}
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className={`w-3.5 h-3.5 rounded-full ${client.rdv_status === 'R1' ? 'bg-slate-800 dark:bg-slate-200' : 'border-2 border-slate-400'}`} />
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">R1</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleRdvChange(client.rdv_status === 'R2' ? '' : 'R2')}
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className={`w-3.5 h-3.5 rounded-full ${client.rdv_status === 'R2' ? 'bg-slate-800 dark:bg-slate-200' : 'border-2 border-slate-400'}`} />
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">R2</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleRdvChange(client.rdv_status === 'R3' ? '' : 'R3')}
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className={`w-3.5 h-3.5 rounded-full ${client.rdv_status === 'R3' ? 'bg-slate-800 dark:bg-slate-200' : 'border-2 border-slate-400'}`} />
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200">R3</span>
                </button>
              </div>

              {/* Cote - Horizontal inline display with radio circles */}
              <div className="flex items-center gap-2 sm:gap-3 border-l border-slate-200 dark:border-slate-700 pl-2 sm:pl-3">
                <span className="text-sm font-medium text-slate-400">Cote</span>
                <button
                  type="button"
                  onClick={() => handleCoteChange(client.cote === 'Glacier' ? '' : 'Glacier')}
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className={`w-3.5 h-3.5 rounded-full ${client.cote === 'Glacier' ? 'bg-blue-500' : 'border-2 border-slate-400'}`} />
                  <span className="text-sm font-medium text-blue-500">Glacier</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleCoteChange(client.cote === 'Froid' ? '' : 'Froid')}
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className={`w-3.5 h-3.5 rounded-full ${client.cote === 'Froid' ? 'bg-red-500' : 'border-2 border-slate-400'}`} />
                  <span className="text-sm font-medium text-red-500">Froid</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleCoteChange(client.cote === 'Tiède' ? '' : 'Tiède')}
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className={`w-3.5 h-3.5 rounded-full ${client.cote === 'Tiède' ? 'bg-yellow-500' : 'border-2 border-slate-400'}`} />
                  <span className="text-sm font-medium text-yellow-500">Tiède</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleCoteChange(client.cote === 'Chaud' ? '' : 'Chaud')}
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <div className={`w-3.5 h-3.5 rounded-full ${client.cote === 'Chaud' ? 'bg-green-500' : 'border-2 border-slate-400'}`} />
                  <span className="text-sm font-medium text-green-500">Chaud</span>
                </button>
              </div>

              {/* Gold Medal toggle */}
              <div className="flex items-center gap-2 sm:gap-3 border-l border-slate-200 dark:border-slate-700 pl-2 sm:pl-3">
                <button
                  type="button"
                  onClick={() => handleGoldMedalChange(!client.gold_medal)}
                  className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <img src="/medal.png" alt="Médaille d'or" className={`w-5 h-5 sm:w-6 sm:h-6 transition-opacity ${client.gold_medal ? 'opacity-100' : 'opacity-30 grayscale'}`} />
                  <span className={`text-sm font-medium ${client.gold_medal ? 'text-amber-600' : 'text-slate-400'}`}>
                    Médaille d&apos;or
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Localisation - Now below the R1/R2/R3 box */}
          <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border sm:border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 sm:mb-6 flex items-center gap-2">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
              Localisation
            </h2>
            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Rue</label>
                <p className="text-slate-900 dark:text-slate-100 font-medium">{client.address || '—'}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Ville</label>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{client.city || '—'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Code Postal</label>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{client.postal_code || '—'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Pays</label>
                  <p className="text-slate-900 dark:text-slate-100 font-medium">{client.country || '—'}</p>
                </div>
              </div>

              {addressComplete && (
                <div className="pt-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowMapPreview((v) => !v)}
                      className={
                        "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                        (showMapPreview
                          ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                      }
                    >
                      <MapIcon className="h-3.5 w-3.5" />
                      Carte
                    </button>
                    <a
                      href={mapsLink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Ouvrir dans Google Maps
                    </a>
                  </div>

                  {showMapPreview && (
                    <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                      <iframe
                        title="Google Maps"
                        src={mapsEmbedSrc}
                        className="w-full h-64"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {false && centralPurchasingId && centralPurchasingName && (
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">Centrale d'Achat</label>
              <button
                onClick={() => navigate(`/achat/clients/${centralPurchasingId}`)}
                className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group"
              >
                <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100 group-hover:underline">
                  {centralPurchasingName}
                </span>
                <ExternalLink className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              </button>
            </div>
          )}

          {/* Centrale d'Achat Section - MOVED UNDER LOCALISATION */}
          {client.client_type !== 'centrale' && client.client_type !== 'central' && (
            <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border sm:border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Link className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  Centrale d'Achat
                </h2>
              </div>

              {client.central_purchasing_id && centralPurchasingName ? (
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Centrale actuelle</label>
                    <p className="text-slate-900 dark:text-slate-100 font-medium">{centralPurchasingName}</p>
                  </div>
                  <button
                    onClick={() => {
                      if (centralBuyers.length === 0) {
                        loadCentralBuyers();
                      }
                      setShowCentralModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors text-sm font-medium"
                  >
                    <Link className="h-4 w-4" />
                    Modifier le rattachement
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-slate-500 dark:text-slate-400 text-sm">Ce client n'est pas rattaché à une centrale d'achat</p>
                  <button
                    onClick={() => {
                      if (centralBuyers.length === 0) {
                        loadCentralBuyers();
                      }
                      setShowCentralModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    <Link className="h-4 w-4" />
                    Affecter à une centrale d'achat
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Billing Data Section - For ALL clients */}
      <div className="mt-6 sm:mt-8">
        <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border sm:border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 sm:mb-6 flex items-center gap-2">
            <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            Données de facturation
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Mode de règlement</label>
              <p className="text-slate-900 dark:text-slate-100 font-medium">{client.payment_mode || '—'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Terme de règlement</label>
              <p className="text-slate-900 dark:text-slate-100 font-medium">{client.payment_terms || '—'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">Email de facturation</label>
              <p className="text-slate-900 dark:text-slate-100 font-medium break-words">{client.invoice_email || client.email || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Images Section - NEW BLOCK */}
      <div className="mt-6 sm:mt-8">
        <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border sm:border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 sm:mb-6 flex items-center gap-2">
            <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            Images
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((photoNum) => {
              const photoUrl = clientImages[`photo_${photoNum}_url` as keyof typeof clientImages];
              return (
                <div key={photoNum} className="relative">
                  <div className="aspect-square bg-slate-100 dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex flex-col items-center justify-center overflow-hidden">
                    {photoUrl ? (
                      <>
                        <img
                          src={photoUrl}
                          alt={`Photo ${photoNum}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-opacity flex items-center justify-center gap-3">
                          {/* Replace button */}
                          <label className="cursor-pointer opacity-0 hover:opacity-100 bg-blue-500 text-white p-2 rounded-full shadow-lg hover:bg-blue-600 transition-colors" title="Remplacer">
                            <Upload className="h-4 w-4" />
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handlePhotoUpload(photoNum, file);
                              }}
                              disabled={uploadingPhoto === photoNum}
                            />
                          </label>
                          {/* Delete button */}
                          <button
                            onClick={() => handlePhotoDelete(photoNum)}
                            className="opacity-0 hover:opacity-100 bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                            title="Supprimer"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                        <Upload className="h-8 w-8 text-slate-400 mb-2" />
                        <span className="text-sm text-slate-500 dark:text-slate-400">Photo {photoNum}</span>
                        <span className="text-xs text-slate-400 mt-1">Cliquer pour uploader</span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handlePhotoUpload(photoNum, file);
                          }}
                          disabled={uploadingPhoto === photoNum}
                        />
                      </label>
                    )}
                    {uploadingPhoto === photoNum && (
                      <div className="absolute inset-0 bg-white dark:bg-slate-900 bg-opacity-80 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Attached Clients Section - ONLY for purchasing centers */}
      {(client.client_type === 'centrale' || client.client_type === 'central') && (
        <div className="mt-6 sm:mt-8">
          <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border sm:border-2 border-slate-200 dark:border-slate-700 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                Clients rattachés
                <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-green-600 rounded-full">
                  {attachedClients.length}
                </span>
              </h2>
              {attachedClients.length > 0 && (
                <button
                  onClick={loadAttachedClients}
                  disabled={loadingAttachedClients}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                >
                  🔄
                </button>
              )}
            </div>

            {loadingAttachedClients ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
              </div>
            ) : attachedClients.length > 0 ? (
              <div className="space-y-3">
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {attachedClients.slice(0, 4).map((attachedClient) => (
                    <div
                      key={attachedClient.id}
                      className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer border border-slate-200 dark:border-slate-700"
                      onClick={() => window.open(`/achat/clients/${attachedClient.id}`, '_blank')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                            {attachedClient.company_name}
                          </h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            {attachedClient.city || '—'}
                          </p>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ml-2 ${attachedClient.status === 'actif'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : attachedClient.status === 'prospect'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                            : attachedClient.status === 'bloque'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              : attachedClient.status === 'inactif'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                          }`}>
                          {attachedClient.status === 'actif' && (
                            <>
                              <span>✓</span>
                              <span>Actif</span>
                            </>
                          )}
                          {attachedClient.status === 'prospect' && (
                            <>
                              <span>👤</span>
                              <span>Prospect</span>
                            </>
                          )}
                          {attachedClient.status === 'bloque' && (
                            <>
                              <span>🚫</span>
                              <span>Bloqué</span>
                            </>
                          )}
                          {attachedClient.status === 'inactif' && (
                            <>
                              <span>⏸️</span>
                              <span>Inactif</span>
                            </>
                          )}
                          {attachedClient.status === 'ferme' && (
                            <>
                              <span>❌</span>
                              <span>Fermé</span>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {attachedClients.length > 4 && (
                  <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={() => window.open(`/achat/clients?central_purchasing_id=${client.id}`, '_blank')}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Voir tous les {attachedClients.length} clients
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/* Compact summary stats */}
                <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                  <div className="grid grid-cols-5 gap-2 text-center">
                    <div>
                      <p className="text-sm font-bold text-green-600 dark:text-green-400">
                        {attachedClients.filter(c => c.status === 'actif').length}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Actifs</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-purple-600 dark:text-purple-400">
                        {attachedClients.filter(c => c.status === 'prospect').length}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Prospects</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-yellow-600 dark:text-yellow-400">
                        {attachedClients.filter(c => c.status === 'inactif').length}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Inactifs</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-red-600 dark:text-red-400">
                        {attachedClients.filter(c => c.status === 'bloque').length}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Bloqués</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-600 dark:text-gray-400">
                        {attachedClients.filter(c => c.status === 'ferme').length}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Fermés</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="mx-auto h-8 w-8 text-slate-400" />
                <h3 className="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">Aucun client rattaché</h3>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Cette centrale n'a pas de clients rattachés.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Central Assignment Modal */}
      {showCentralModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <Link className="h-5 w-5 text-purple-600" />
                  {client.central_purchasing_id ? 'Modifier le rattachement' : 'Affecter à une centrale'}
                </h3>
                <button
                  onClick={() => setShowCentralModal(false)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Sélectionner une centrale d'achat
                  </label>
                  {loadingCentrals ? (
                    <div className="flex items-center justify-center p-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      <button
                        onClick={() => handleCentralAssignment(null)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${!client.central_purchasing_id
                          ? 'border-purple-200 bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300'
                          : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-100'
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${!client.central_purchasing_id ? 'border-purple-600 bg-purple-600' : 'border-slate-400'
                            }`}>
                            {!client.central_purchasing_id && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <span className="font-medium">Aucune centrale</span>
                        </div>
                      </button>

                      {centralBuyers.map((central) => (
                        <button
                          key={central.id}
                          onClick={() => handleCentralAssignment(central.id)}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${client.central_purchasing_id === central.id
                            ? 'border-purple-200 bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300'
                            : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-100'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${client.central_purchasing_id === central.id ? 'border-purple-600 bg-purple-600' : 'border-slate-400'
                              }`}>
                              {client.central_purchasing_id === central.id && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            <span className="font-medium">{central.company_name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => setShowCentralModal(false)}
                    className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
